/**
 * AdLtvWidget — T5 «Ad LTV curves by cohort»
 * =============================================
 *
 * Спека: .claude/plans/2026-08-25-avz-ad-monetization-dashboards.md, T5.
 *
 * КОГОРТНАЯ вьюха: диапазон дат дашборда трактуется как окно РЕГИСТРАЦИИ
 * (reg_date), не события — сервер сам считает по этой оси (см. adLtvService).
 *
 * Два графика:
 * 1) Мультилинейные кривые накопительного Ad LTV_d (d = 0..180), ≤6 когорт,
 *    легенда + прямая подпись у правого края каждой линии.
 * 2) Треугольный heatmap когорта × день: закрашены только точки, до которых
 *    когорта уже дожила (иначе значение было бы искусственным нулём).
 *
 * 58% сегодняшней рекламной выручки приходит от игроков старше 30 дней —
 * поэтому ось X всегда идёт до D180, а не обрывается на D30.
 */

import React, { useMemo, useState } from 'react';
import { BaseWidget, useBaseWidget } from '../BaseWidget';
import { useAdsWidgetData } from '../../../hooks/useAdsGeo';
import { AdsService } from '../../../services/adsService';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Legend } from 'recharts';
import { Tooltip } from '../../charts/OrderedTooltip';
import { ResponsiveChart as ResponsiveContainer } from '../../ui/responsive-chart';
import { adsLtv as adsLtvSpecs } from '@ggstat/chart-specs';

// Палитра когорт и цвет heatmap — общие с MCP-рендером (shared/chart-specs/specs/adsLtv.json)
const COHORT_COLORS = adsLtvSpecs.ltv.cohortColors;
const HEAT_RGB = [1, 3, 5].map((i) => parseInt(adsLtvSpecs.ltv.heatColor.slice(i, i + 2), 16)).join(',');

const fmtUsd = (v) =>
  Number(v || 0).toLocaleString('ru-RU', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });

/** Дней между YYYY-MM-DD ('вчера' как "сегодня") — сколько когорта уже прожила. */
function daysSince(dateStr) {
  const start = new Date(`${dateStr}T00:00:00Z`);
  const yesterday = new Date();
  yesterday.setUTCDate(yesterday.getUTCDate() - 1);
  yesterday.setUTCHours(0, 0, 0, 0);
  return Math.floor((yesterday.getTime() - start.getTime()) / 86400000);
}

/** Разворачивает cohorts[].points[] в строки {d, [cohortKey]: ltv} для мультилинейного графика. */
function buildCurveRows(cohorts) {
  const byD = new Map();
  for (const cohort of cohorts) {
    for (const point of cohort.points) {
      const row = byD.get(point.d) || { d: point.d };
      row[cohort.cohortKey] = point.ltv;
      byD.set(point.d, row);
    }
  }
  return Array.from(byD.values()).sort((a, b) => a.d - b.d);
}

function LtvTooltip({ active, payload, label, cohorts }) {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div className="bg-background border rounded-lg shadow-lg p-3 text-sm space-y-1">
      <p className="font-medium">D{label}</p>
      {payload.map((entry) => {
        const cohort = cohorts.find((c) => c.cohortKey === entry.dataKey);
        return (
          <p key={entry.dataKey} style={{ color: entry.color }}>
            {entry.dataKey} ({cohort ? cohort.cohortSize.toLocaleString('ru-RU') : '?'} игроков): {fmtUsd(entry.value)}
          </p>
        );
      })}
    </div>
  );
}

/** Прямая подпись у правого края линии — только на последней точке (D180). */
function renderEndLabel(cohortKey, color, lastIndex) {
  return ({ x, y, index }) => {
    if (index !== lastIndex) return null;
    return (
      <text x={x + 6} y={y} dy={4} fontSize={11} fill={color}>
        {cohortKey}
      </text>
    );
  };
}

function AdLtvCurvesChart({ cohorts }) {
  const rows = buildCurveRows(cohorts);
  if (rows.length === 0) return null;
  const lastIndex = rows.length - 1;

  return (
    <ResponsiveContainer width="100%" height={360}>
      <LineChart data={rows} margin={{ top: 8, right: 64, bottom: 8, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
        <XAxis
          dataKey="d"
          type="category"
          tick={{ fill: 'currentColor' }}
          className="text-xs"
          label={{ value: 'День с регистрации (D)', position: 'insideBottom', offset: -2 }}
        />
        <YAxis
          tick={{ fill: 'currentColor' }}
          className="text-xs"
          label={{ value: 'Ad LTV_d, $/установку', angle: -90, position: 'insideLeft' }}
        />
        <Tooltip content={<LtvTooltip cohorts={cohorts} />} />
        <Legend />
        {cohorts.map((cohort, i) => (
          <Line
            key={cohort.cohortKey}
            type="monotone"
            dataKey={cohort.cohortKey}
            name={cohort.cohortKey}
            stroke={COHORT_COLORS[i % COHORT_COLORS.length]}
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
            connectNulls
            label={renderEndLabel(cohort.cohortKey, COHORT_COLORS[i % COHORT_COLORS.length], lastIndex)}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}

/** Одноцветная шкала (насыщенность голубого) — 0 = прозрачно, max в таблице = насыщенный. */
function heatColor(value, max) {
  if (value == null) return 'transparent';
  if (!max) return `rgba(${HEAT_RGB},0.08)`;
  const ratio = Math.max(0, Math.min(1, value / max));
  const alpha = 0.08 + ratio * 0.82;
  return `rgba(${HEAT_RGB},${alpha.toFixed(3)})`;
}

/**
 * Треугольный heatmap когорта × день: ячейка закрашена только если когорта
 * уже дожила до дня d (иначе это не "нулевой LTV", а "данных пока нет").
 */
function AdLtvHeatmap({ cohorts, days }) {
  if (cohorts.length === 0) return null;
  const maxLtv = Math.max(0, ...cohorts.flatMap((c) => c.points.map((p) => p.ltv)));

  return (
    <div className="overflow-x-auto">
      <table className="text-xs border-collapse">
        <thead>
          <tr>
            <th className="text-left pr-3 pb-1 font-medium text-muted-foreground">Когорта</th>
            {days.map((d) => (
              <th key={d} className="px-2 pb-1 font-medium text-muted-foreground text-center">
                D{d}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {cohorts.map((cohort) => {
            const ageDays = daysSince(cohort.cohortKey);
            return (
              <tr key={cohort.cohortKey}>
                <td className="pr-3 py-1 whitespace-nowrap">{cohort.cohortKey}</td>
                {days.map((d) => {
                  const matured = d <= ageDays;
                  const point = cohort.points.find((p) => p.d === d);
                  const value = matured ? point?.ltv ?? 0 : null;
                  return (
                    <td
                      key={d}
                      className="px-2 py-1 text-center border border-border/40"
                      style={{ backgroundColor: heatColor(value, maxLtv) }}
                      title={value != null ? fmtUsd(value) : 'ещё не дожила'}
                    >
                      {value != null ? value.toFixed(2) : '—'}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

async function fetchAdLtv(filters, extra, options) {
  return AdsService.get('/ltv', filters, extra, options);
}

export function AdLtvWidget() {
  const [granularity, setGranularity] = useState('week');

  const ltvData = useAdsWidgetData(
    (filters, options) => fetchAdLtv(filters, { granularity, maxCohorts: 6 }, options),
    [granularity]
  );
  const widgetProps = useBaseWidget(ltvData);
  const { data } = widgetProps;

  const cohorts = data?.cohorts || [];
  const days = data?.days || [];
  const unknownCohort = data?.meta?.unknownCohort;

  const footnote = useMemo(() => {
    if (!unknownCohort) return null;
    return (
      `Когорты с reg_date = 1970-01-01 («когорта неизвестна») исключены: ` +
      `$${unknownCohort.revenue.toFixed(2)} (${(unknownCohort.shareOfCalendarRevenue * 100).toFixed(2)}% выручки окна)`
    );
  }, [unknownCohort]);

  return (
    <BaseWidget
      {...widgetProps}
      title="Ad LTV по когортам"
      subtitle="Накопительная выручка от рекламы на установку, D0–D180 — 58% сегодняшней выручки приходит от игроков старше D30"
      emptyMessage="Нет данных за выбранное окно регистрации"
      skeletonRows={8}
      headerAction={
        <div className="flex gap-1 text-xs">
          {['week', 'day'].map((g) => (
            <button
              key={g}
              onClick={() => setGranularity(g)}
              className={
                'px-2 py-1 rounded border ' +
                (granularity === g ? 'bg-primary text-primary-foreground border-primary' : 'border-border')
              }
            >
              {g === 'week' ? 'по неделям' : 'по дням'}
            </button>
          ))}
        </div>
      }
    >
      <div className="space-y-6">
        <AdLtvCurvesChart cohorts={cohorts} />

        <div>
          <p className="text-sm font-medium mb-2">Ad LTV_d — когорта × день (треугольный heatmap)</p>
          <AdLtvHeatmap cohorts={cohorts} days={days} />
        </div>

        {footnote && <p className="text-xs text-muted-foreground">{footnote}</p>}
      </div>
    </BaseWidget>
  );
}

export default AdLtvWidget;
